import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { Suspense, useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { UpdateWebhookSheetFallback } from '@/routes/webhook/tabs/overview/update-webhook-sheet/fallback'
import { WEBHOOK_EVENT_TYPES } from '@/routes/webhooks/eventTypes'
import type { EventType } from '@/graphql.types'
import type {
  UpdateWebhookMutation,
  UpdateWebhookMutationVariables,
  WebhookQuery,
  WebhookQueryVariables,
} from '@/routes/webhook/tabs/overview/update-webhook-sheet/index.types.gen'

const schema = z.object({
  allowedEvents: z
    .array(z.enum(WEBHOOK_EVENT_TYPES))
    .min(1, {
      message: 'You must select at least one event type',
    })
    .default([]),
  endpoint: z
    .url({ message: 'Please enter a valid URL' })
    .min(1, { message: 'URL is required' }),
  description: z.string().optional(),
})

const WEBHOOK_QUERY = gql`
  query webhook($idCompany: String!, $id: String!) {
    company(id: $idCompany) {
      id
      webhook(id: $id) @live {
        id
        endpoint
        description
        allowedEvents
      }
    }
  }
`

const UPDATE_WEBHOOK = gql`
  mutation UpdateWebhook(
    $id: String!
    $allowedEvents: [EventType!]!
    $endpoint: String!
    $description: String
  ) {
    updateWebhook(
      id: $id
      allowedEvents: $allowedEvents
      endpoint: $endpoint
      description: $description
    ) {
      id
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const Content = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const { idWebhook = '' } = useParams()
  const [isPending, startTransition] = useTransition()

  const { data } = useSuspenseQuery<WebhookQuery, WebhookQueryVariables>(
    WEBHOOK_QUERY,
    { variables: { idCompany, id: idWebhook } }
  )
  const webhook = data?.company?.webhook

  const [updateWebhook] = useMutation<
    UpdateWebhookMutation,
    UpdateWebhookMutationVariables
  >(UPDATE_WEBHOOK)

  const form = useForm<z.input<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      endpoint: webhook?.endpoint ?? '',
      description: webhook?.description ?? '',
      allowedEvents: webhook?.allowedEvents ?? [],
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = form

  useEffect(() => {
    if (!isOpen) {
      reset({
        endpoint: '',
        description: '',
        allowedEvents: [],
      })
    }
  }, [isOpen, reset])

  const onSubmit = async (values: z.input<typeof schema>) => {
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        await updateWebhook({
          variables: {
            id: idWebhook,
            endpoint: values.endpoint,
            description: values.description,
            allowedEvents: values.allowedEvents as EventType[],
          },
        })
        toast(
          <ToasterContent variant="success">
            Webhook updated successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to update webhook
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <div className="px-4 overflow-y-auto w-full pb-2 h-full">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="endpoint"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Endpoint</FormLabel>
                  <FormDescription>
                    Webhooks require a URL to send events to
                  </FormDescription>
                  <FormControl>
                    <Input
                      placeholder="https://"
                      autoComplete="off"
                      aria-label="Webhook endpoint URL"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="An optional description of the webhook"
                      autoComplete="off"
                      aria-label="Webhook description"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="allowedEvents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Allowed events</FormLabel>
                  <FormControl>
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2 mb-4">
                        <Checkbox
                          id="select-all-types"
                          checked={
                            field?.value?.length === WEBHOOK_EVENT_TYPES.length
                          }
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([...WEBHOOK_EVENT_TYPES])
                            } else {
                              field.onChange([])
                            }
                          }}
                        />
                        <FormLabel htmlFor="select-all-types">
                          Select all
                        </FormLabel>
                      </div>
                      {WEBHOOK_EVENT_TYPES.map((eventType) => (
                        <div
                          className="flex items-center gap-2"
                          key={eventType}
                        >
                          <Checkbox
                            id={eventType}
                            value={eventType}
                            checked={field?.value?.includes?.(eventType)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([
                                  ...(field.value || []),
                                  eventType,
                                ])
                              } else {
                                field.onChange(
                                  (field.value || []).filter(
                                    (value) => value !== eventType
                                  )
                                )
                              }
                            }}
                          />
                          <FormLabel className="font-mono" htmlFor={eventType}>
                            {eventType}
                          </FormLabel>
                        </div>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" hidden />
          </form>
        </Form>
      </div>
      <SheetFooter className="sticky">
        <Button disabled={isPending} onClick={handleSubmit(onSubmit)}>
          {isPending ? <Loader className="animate-spin" /> : null}
          Update webhook
        </Button>
      </SheetFooter>
    </>
  )
}

export const UpdateWebhookSheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Update webhook</SheetTitle>
        </SheetHeader>
        <Suspense fallback={<UpdateWebhookSheetFallback />}>
          <Content isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
